import { DrawerSurfaceEntryContext } from '../contexts/DrawerSurfaceEntryContext'
import type { MapCameraPosition } from 'tripPlanner/api/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import noop from 'lib/function/noop'
import usePrevious from 'hooks/usePrevious'
import { HotelListingDrawer, type DrawerHotel } from '../components/HotelListingDrawer'
import ExperienceListingDrawer from '../components/ExperienceListingDrawer/ExperienceListingDrawer'
import type { DrawerExperience } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import TripPlanDetailDrawer from '../components/TripPlanDetailDrawer'
import SmartPlaceDrawer from '../components/SmartPlaceDrawer'
import PendingPlaceDrawer from '../components/PendingPlaceDrawer'
import LocationInfoDrawer from '../components/LocationInfoDrawer'
import ChatOfferDetailDrawer from '../components/ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from '../components/ChatExperienceDetailDrawer'
import ChatCruiseOfferDetailDrawer from '../components/CruiseOfferDetailDrawer'
import { useDrawerStackState } from '../hooks/useDrawerStackState'
import { useTripPlannerEventBus, useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { usePlaceSearchFailure } from 'tripPlanner/assistant/contexts/PlaceSearchFailureContext'
import { useRightPanelTripDrawerPresentation } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import { usePlaceSearchResolver } from '../hooks/usePlaceSearchResolver'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { findStopById, getStopCoordinates } from 'tripPlanner/utils/chatItinerary'
import { trackHotelDrawerOpened, trackExperienceDrawerOpened, trackCruiseDrawerOpened } from 'tripPlanner/utils/chatAnalytics'
import { DrawerSnapshotRegistration } from '../contexts/DrawerSnapshotRegistration'

import { buildPlaceDrawerOpenedActivityEntry } from './buildPlaceDrawerOpenedActivityEntry'
import { buildStopDeletedActivityEntry } from '../utils/buildStopDeletedActivityEntry'

interface DrawerContainerProps {
  closeSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
  /** Mobile only: the drawer is the whole screen, so closing it returns them to the map. */
  keepOpenAfterItineraryAdd?: boolean
}

function DrawerContainer({
  closeSignal,
  onDrawerOpenChange = noop,
  keepOpenAfterItineraryAdd = false,
}: DrawerContainerProps = {}) {
  const eventBus = useTripPlannerEventBus()
  const [hasDomainDrawerBase, setHasDomainDrawerBase] = useState(false)
  const { sendMessage } = useChatActions()
  const { chatId, isStreaming, isStreamOpen } = useChatStreamStatus()
  const { itineraryData, multiCartId, deleteManualItineraryTarget, isManualItineraryDeletePending } = useItineraryContext()
  const manualDeleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending

  const {
    topEntry,
    previousEntry,
    isAnyDrawerOpen,
    hasPreviousDrawer,
    shouldAnimateTopEntry,
    openDetailPanel,
    openPlaceInfo,
    openPlaceSearchPending,
    resolvePlaceSearchPending,
    dismissPlaceSearchPending,
    openLocationInfo,
    openOfferDrawer,
    openListingSearch,
    goBack,
    closeAllDrawers,
    afterClose,
    resetDrawers,
  } = useDrawerStackState()
  const [pendingSmartPlaceDrawerId, setPendingSmartPlaceDrawerId] = useState<string | undefined>(undefined)
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const showImmediatePlaceDrawerFeedback = drawerPresentation === 'modal'
  const checkoutSource = useMemo<App.CheckoutSource | undefined>(() => {
    if (!chatId || !multiCartId) return undefined
    return {
      type: 'trip_planner',
      multiCartId,
      externalReferenceId: String(chatId),
    }
  }, [chatId, multiCartId])

  const { resetFailures } = usePlaceSearchFailure()
  const handlePlaceSearchResolved = useCallback((googlePlaceId: string, placeName: string) => {
    resolvePlaceSearchPending({ googlePlaceId, placeName })
    if (!showImmediatePlaceDrawerFeedback) {
      openPlaceInfo({ googlePlaceId, placeName })
    }
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(placeName, 'trip_planner.chat.place_search_link', googlePlaceId)],
    })
  }, [eventBus, openPlaceInfo, resolvePlaceSearchPending, showImmediatePlaceDrawerFeedback])
  const { resolvePlace, cancel: cancelResolve } = usePlaceSearchResolver(
    handlePlaceSearchResolved,
    dismissPlaceSearchPending,
  )
  const previousCloseSignal = usePrevious(closeSignal, closeSignal)
  // Domain drawers (e.g. the List's stop search) count as open too, so mobile Back closes them before leaving the chat.
  const isAnyDrawerSurfaceOpen = isAnyDrawerOpen || hasDomainDrawerBase
  const previousIsAnyDrawerSurfaceOpenRef = useRef(isAnyDrawerSurfaceOpen)

  const closeDomainDrawers = useCallback(() => {
    setHasDomainDrawerBase(false)
    eventBus.dispatch({ type: 'close_right_panel_domain_drawers' })
  }, [eventBus])

  const handleSmartPlaceDrawerPendingChange = useCallback((entryId: string, isPending: boolean) => {
    setPendingSmartPlaceDrawerId(current => {
      if (isPending) return entryId
      return current === entryId ? undefined : current
    })
  }, [])

  useEffect(() => {
    if (!pendingSmartPlaceDrawerId || topEntry?.id === pendingSmartPlaceDrawerId) return

    setPendingSmartPlaceDrawerId(undefined)
  }, [pendingSmartPlaceDrawerId, topEntry?.id])

  useEffect(() => {
    if (previousIsAnyDrawerSurfaceOpenRef.current === isAnyDrawerSurfaceOpen) return

    previousIsAnyDrawerSurfaceOpenRef.current = isAnyDrawerSurfaceOpen
    onDrawerOpenChange(isAnyDrawerSurfaceOpen)
  }, [isAnyDrawerSurfaceOpen, onDrawerOpenChange])

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignal) return

    cancelResolve()
    resetDrawers()
    closeDomainDrawers()
  }, [cancelResolve, closeDomainDrawers, closeSignal, previousCloseSignal, resetDrawers])

  const trackedOfferDrawerIdsRef = useRef(new Set<string>())
  useEffect(() => {
    const isOfferDrawerOpen = isAnyDrawerOpen && topEntry?.kind === 'offerDrawer'
    const justOpened = isOfferDrawerOpen && !trackedOfferDrawerIdsRef.current.has(topEntry.id)
    if (!justOpened || !chatId) return

    trackedOfferDrawerIdsRef.current.add(topEntry.id)
    if (topEntry.data.offerType === 'experience') {
      trackExperienceDrawerOpened(chatId)
    } else if (topEntry.data.offerType === 'cruise') {
      trackCruiseDrawerOpened(chatId)
    } else {
      trackHotelDrawerOpened(chatId)
    }
  }, [isAnyDrawerOpen, topEntry, chatId])

  const handleDeleteStopFromDrawer = useCallback(async(stopId: number) => {
    if (manualDeleteActionsDisabled) return
    const stop = findStopById(stopId, itineraryData)
    if (!stop) return
    // Captured before the delete request round-trips below, not after.
    const occurredAt = new Date().toISOString()
    const deleted = await deleteManualItineraryTarget({ targetType: 'stop', stopId: stop.id }, stop.placeName)
    if (deleted) {
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildStopDeletedActivityEntry(stop.placeName, 'trip_planner.drawer.delete_stop', occurredAt)],
      })
    }
  }, [manualDeleteActionsDisabled, itineraryData, deleteManualItineraryTarget, eventBus])

  const closeEveryDrawer = useCallback(() => {
    cancelResolve()
    closeAllDrawers()
    closeDomainDrawers()
  }, [cancelResolve, closeAllDrawers, closeDomainDrawers])

  useEventSubscription('itinerary_item_added', () => {
    if (keepOpenAfterItineraryAdd) return
    closeEveryDrawer()
  })

  useEventSubscription('itinerary_stop_added', () => {
    if (keepOpenAfterItineraryAdd) return
    closeEveryDrawer()
  })

  useEventSubscription('view_itinerary_item', () => {
    closeEveryDrawer()
  })

  const handleOfferShowMap = useCallback((hotel: DrawerHotel, camera?: MapCameraPosition) => {
    closeEveryDrawer()
    if (!hotel.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      camera,
      data: {
        offerId: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation,
        offerType: 'hotel',
      },
    })
  }, [closeEveryDrawer, eventBus])

  const handleExperienceDrawerShowMap = useCallback((experience: DrawerExperience, camera?: MapCameraPosition) => {
    closeEveryDrawer()
    if (!experience.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      camera,
      data: {
        offerId: experience.offerId,
        name: experience.name,
        coordinates: experience.geoLocation,
        offerType: 'experience',
      },
    })
  }, [closeEveryDrawer, eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('open_place_drawer', (event) => {
    openPlaceInfo({ googlePlaceId: event.googlePlaceId, placeName: event.placeName })
  })

  useEventSubscription('open_place_search_drawer', (event) => {
    if (showImmediatePlaceDrawerFeedback) {
      openPlaceSearchPending({
        searchQuery: event.searchQuery,
        placeName: event.placeName,
      })
    }
    resolvePlace({ searchQuery: event.searchQuery, placeName: event.placeName })
  })

  useEventSubscription('open_location_drawer', (event) => {
    openLocationInfo(event.data)
  })

  useEventSubscription('open_detail_drawer', (event) => {
    openDetailPanel({
      googlePlaceId: event.googlePlaceId,
      placeName: event.placeName,
      countryName: event.countryName,
      stopId: event.stopId,
    })
  })

  useEventSubscription('open_listing_search_drawer', (event) => {
    openListingSearch({
      vertical: event.vertical,
      placeName: event.placeName,
      placeCountry: event.placeCountry,
      placeId: event.placeId,
      searchParams: event.searchParams,
      stopId: event.stopId,
    })
  })

  useEventSubscription('open_offer_drawer', (event) => {
    openOfferDrawer({
      offerId: event.offerId,
      accommodationPropertyId: event.accommodationPropertyId,
      searchParams: event.searchParams,
      offerType: event.offerType,
      scrollToPricing: event.scrollToPricing,
      scrollToRoomOptions: event.scrollToRoomOptions,
    })
  })

  const handleStackBackNavigation = useCallback(() => {
    if (topEntry?.kind === 'placeSearchPending') {
      cancelResolve()
    }
    if (hasPreviousDrawer) {
      goBack()
      return
    }
    if (hasDomainDrawerBase) {
      closeAllDrawers()
    }
  }, [
    cancelResolve,
    closeAllDrawers,
    goBack,
    hasDomainDrawerBase,
    hasPreviousDrawer,
    topEntry?.kind,
  ])

  const handleStackBack = isAnyDrawerOpen && (hasPreviousDrawer || hasDomainDrawerBase) ?
    handleStackBackNavigation :
    undefined

  const centralDrawerAnimation = shouldAnimateTopEntry && !hasDomainDrawerBase ? undefined : 'none'
  const shouldDeferPendingSmartPlaceDrawer = !showImmediatePlaceDrawerFeedback &&
    topEntry?.kind === 'placeInfo' &&
    topEntry.id === pendingSmartPlaceDrawerId
  const visibleEntry = shouldDeferPendingSmartPlaceDrawer ? previousEntry : topEntry
  const listingSearchStop = findStopById(
    visibleEntry?.kind === 'listingSearch' ? visibleEntry.data.stopId : undefined,
    itineraryData,
  )
  const visibleDrawerAnimation = visibleEntry?.id === topEntry?.id ? centralDrawerAnimation : 'none'

  useEventSubscription('right_panel_domain_drawer_state_changed', (event) => {
    setHasDomainDrawerBase(event.isOpen)
  })

  useEventSubscription('reset_chat_state', () => {
    cancelResolve()
    resetDrawers()
    closeDomainDrawers()
    resetFailures()
  })

  return (
    <DrawerSurfaceEntryContext.Provider value={visibleEntry?.id}>
      <DrawerSnapshotRegistration isAnyDrawerOpen={isAnyDrawerOpen} topEntry={visibleEntry} />
      {shouldDeferPendingSmartPlaceDrawer && topEntry?.kind === 'placeInfo' && (
        <SmartPlaceDrawer
          key={topEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation="none"
          googlePlaceId={topEntry.data.googlePlaceId}
          placeName={topEntry.data.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          chatId={chatId}
          drawerEntryId={topEntry.id}
          renderWhenReady={false}
          onPendingChange={handleSmartPlaceDrawerPendingChange}
        />
      )}
      {visibleEntry?.kind === 'detailPanel' && (
        <TripPlanDetailDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onDismiss={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          animation={visibleDrawerAnimation}
          googlePlaceId={visibleEntry.data.googlePlaceId}
          placeName={visibleEntry.data.placeName}
          countryName={visibleEntry.data.countryName}
          stopId={visibleEntry.data.stopId}
          onDeleteStop={visibleEntry.data.stopId !== undefined && !manualDeleteActionsDisabled ? handleDeleteStopFromDrawer : undefined}
          chatId={chatId}
        />
      )}
      {visibleEntry?.kind === 'placeSearchPending' && (
        <PendingPlaceDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          placeName={visibleEntry.data.placeName}
        />
      )}
      {visibleEntry?.kind === 'placeInfo' && (
        <SmartPlaceDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          googlePlaceId={visibleEntry.data.googlePlaceId}
          placeName={visibleEntry.data.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          chatId={chatId}
          drawerEntryId={visibleEntry.id}
          onPendingChange={handleSmartPlaceDrawerPendingChange}
        />
      )}
      {visibleEntry?.kind === 'locationInfo' && (
        <LocationInfoDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          locationData={visibleEntry.data}
          category={visibleEntry.data.category}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {visibleEntry?.kind === 'listingSearch' && visibleEntry.data.vertical === 'hotel' && (
        <HotelListingDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          placeName={visibleEntry.data.placeName}
          placeCountry={visibleEntry.data.placeCountry}
          placeId={visibleEntry.data.placeId}
          seedSearchFromPlace
          initialSearchParams={visibleEntry.data.searchParams}
          contextStop={listingSearchStop}
          stopCoordinates={getStopCoordinates(listingSearchStop)}
          onShowMap={handleOfferShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {visibleEntry?.kind === 'listingSearch' && visibleEntry.data.vertical === 'experience' && (
        <ExperienceListingDrawer
          key={visibleEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          placeName={visibleEntry.data.placeName}
          placeCountry={visibleEntry.data.placeCountry}
          placeId={visibleEntry.data.placeId}
          contextStop={listingSearchStop}
          stopCoordinates={getStopCoordinates(listingSearchStop)}
          seedSearchFromPlace
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {visibleEntry?.kind === 'offerDrawer' && visibleEntry.data.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          key={visibleEntry.id}
          offerId={visibleEntry.data.offerId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          onShowMap={handleExperienceDrawerShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          scrollToPricing={visibleEntry.data.scrollToPricing}
          checkoutSource={checkoutSource}
        />
      )}
      {visibleEntry?.kind === 'offerDrawer' && visibleEntry.data.offerType === 'cruise' && (
        <ChatCruiseOfferDetailDrawer
          key={visibleEntry.id}
          offerId={visibleEntry.data.offerId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          itineraryData={itineraryData}
          isStreaming={isStreaming || isStreamOpen}
          onSendMessage={sendMessage}
        />
      )}
      {visibleEntry?.kind === 'offerDrawer' && visibleEntry.data.offerType !== 'experience' && visibleEntry.data.offerType !== 'cruise' && (
        <ChatOfferDetailDrawer
          key={visibleEntry.id}
          offerId={visibleEntry.data.offerId}
          accommodationPropertyId={visibleEntry.data.accommodationPropertyId}
          offerType={visibleEntry.data.offerType}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={visibleDrawerAnimation}
          onShowMap={handleOfferShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          searchParams={visibleEntry.data.searchParams}
          scrollToRoomOptions={visibleEntry.data.scrollToRoomOptions}
          checkoutSource={checkoutSource}
        />
      )}
    </DrawerSurfaceEntryContext.Provider>
  )
}

export default DrawerContainer
